import { useEffect, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  Map,
  useMediaQuery,
  useRouter,
  useI18n,
} from '@popety_io/popety-io-lib'
import { useSelector } from 'react-redux'
import { estimateSelector } from '../../../redux'
import { landSelector } from '../../../../LandDetail/redux/summary'
import { getCompanyTiles, removeCompanyTiles } from './utils'

const fillStyle = 'layer_fill_company'

interface EstimateCompanyControllerProps {
  map: Map
  loaded?: boolean
}

const EstimateCompanyController = ({
  map,
  loaded,
}: EstimateCompanyControllerProps) => {
  const estimate: any = useSelector(estimateSelector)
  const land: any = useSelector(landSelector)
  const { query } = useRouter()
  const { t } = useI18n()

  const [popup, setPopup] = useState<mapboxgl.Popup>()
  const isCompanyTab = query.subTab === 'company'
  const touchScreen = useMediaQuery('(hover: none)')

  useEffect(() => {
    if (touchScreen) setPopup(undefined)
  }, [touchScreen])

  useEffect(() => {
    setPopup(
      new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      }),
    )
  }, [])

  useEffect(() => {
    if (query.subTab !== 'company' && map && loaded) {
      removeCompanyTiles({ map })
    }
  }, [query.subTab, query.tab])

  const addTiles = () => {
    try {
      getCompanyTiles({
        map,
        fieldOpt: query.companyLayer,
      })
    } catch (e) {
      console.error(e)
    }
  }

  const handleMouseMove = (e: any) => {
    map.getCanvas().style.cursor = 'pointer'

    if (popup) {
      const feature = e?.features?.[0]

      if (!feature) return

      let str = ''

      if (
        query.companyLayer === 'total_establishments' &&
        feature.properties.total_establishments !== 0
      ) {
        str = `${Math.round(feature.properties.total_establishments)} ${t('common.establishment')}`
      } else if (
        query.companyLayer === 'total_employment' &&
        feature.properties.total_employment !== 0
      ) {
        str = `${Math.round(feature.properties.total_employment)} ${t('common.employer')}`
      } else if (
        query.companyLayer === 'total_employment_most_represented_sector' &&
        feature.properties.total_employment_most_represented_sector !== 0
      ) {
        str = t(
          `common.company_${feature.properties.total_employment_most_represented_sector?.split('_').slice(-2).join('_')}`,
        )
      } else if (
        query.companyLayer === 'total_establishments_most_represented_sector' &&
        feature.properties.total_establishments_most_represented_sector !== 0
      ) {
        str = t(
          `common.company_${feature.properties.total_establishments_most_represented_sector?.split('_').slice(-2).join('_')}`,
        )
      } else {
        str = `${Math.round(feature.properties.total_employment)} ${t('common.employer')}`
      }

      popup.setLngLat(e.lngLat).setHTML(str).addTo(map)
    }
  }

  const handleMouseLeave = () => {
    map.getCanvas().style.cursor = ''

    if (popup) {
      popup.remove()
    }
  }

  useEffect(() => {
    if (query.subTab !== 'company' || !map || !loaded) return

    addTiles()

    map.on('style.load', addTiles)
    map.on('mousemove', fillStyle, handleMouseMove)
    map.on('mouseleave', fillStyle, handleMouseLeave)

    return () => {
      map.off('style.load', addTiles)
      map.off('mousemove', fillStyle, handleMouseMove)
      map.off('mouseleave', fillStyle, handleMouseLeave)
    }
  }, [isCompanyTab, loaded, estimate, land, query.companyLayer])

  if (!isCompanyTab || !map || !loaded) return null

  return null
}

export default EstimateCompanyController
